import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { getMySubmission } from '../../services/intakeService';
import { useAuth } from '../../hooks/useAuth';
import OfferLoadingScreen from '../../components/common/OfferLoadingScreen';
import ConsentDisclaimer from '../../components/common/ConsentDisclaimer';
import styles from './SingleProductOfferPage.module.css';
import SupplyMonthSelector from '../../components/product/SupplyMonthSelector';
import { applyPromoDiscount, getAdjustedSupplyPricing } from '../../utils/promoUtils';
import { resolveLoadErrorMessage } from '../../utils/errorMessages';

const SingleProductOfferPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromIntake = searchParams.get('from') === 'intake';
  const { user } = useAuth();
  const [offer, setOffer] = useState(null);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkingIntake, setCheckingIntake] = useState(false);
  const [supplyMonths, setSupplyMonths] = useState(1);
  const [selectedGalleryIndex, setSelectedGalleryIndex] = useState(0);

  // Check if loading screen has already been shown this session
  const sessionKey = `productOffer_loadingShown_${productId}`;
  const hasShownLoading = sessionStorage.getItem(sessionKey) === 'true';
  const [animationDone, setAnimationDone] = useState(hasShownLoading);

  const handleAnimationComplete = useCallback(() => {
    setAnimationDone(true);
    sessionStorage.setItem(sessionKey, 'true');
  }, [sessionKey]);

  const showLoading = !animationDone || loading;

  useEffect(() => {
    const loadOffer = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/offers/product/${productId}`);
        
        if (!response.offer) {
          setError('No offer found for this product');
          return;
        }
        
        setOffer(response.offer);
        setProduct(response.offer.product);
      } catch (err) {
        console.error('Failed to load product offer:', err);
        setError(resolveLoadErrorMessage(err, 'Failed to load offer'));
      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      loadOffer();
    }
  }, [productId]);

  // Navigate to checkout with product
  const navigateToCheckout = useCallback((productSlug) => {
    sessionStorage.setItem('selectedSupplyMonths', supplyMonths.toString());
    navigate(`/calendar?product=${productSlug}&supplyMonths=${supplyMonths}`);
  }, [navigate, supplyMonths]);

  // Handle CTA click with intake check
  const handleCTAClick = useCallback(async () => {
    if (!product?.slug || !product?.id) return;

    // If user is not logged in, send to intake page
    if (!user) {
      navigate(`/intake/${product.slug}`);
      return;
    }

    // If user just came from intake, go straight to checkout
    if (fromIntake) {
      navigateToCheckout(product.slug);
      return;
    }

    // Check if user has completed intake for this product
    setCheckingIntake(true);
    try {
      const submission = await getMySubmission(product.id);
      setCheckingIntake(false);

      if (submission) {
        navigateToCheckout(product.slug);
      } else {
        navigate(`/intake/${product.slug}`);
      }
    } catch (err) {
      setCheckingIntake(false);
      // `services/api.js` throws an Error carrying `status` (:255) and NEVER populates the
      // axios-shaped `err.response`, so the old `err.response?.status === 404` could not be
      // satisfied and this whole branch was dead code. A 404 here is the server ANSWERING
      // "no submission on file"; any other failure means we could not ask at all. Both must
      // route to intake -- sending an unverified customer to checkout fails the clinical
      // gate OPEN, which is the defect this replaces.
      if (err?.status !== 404) {
        console.error('Failed to check intake submission:', err);
      }
      navigate(`/intake/${product.slug}`);
    }
  }, [product, user, fromIntake, navigate, navigateToCheckout]);

  // Get all images for gallery
  const getAllImages = () => {
    const images = [];
    if (offer?.heroImage) {
      images.push({ url: offer.heroImage, type: 'hero' });
    }
    if (offer?.galleryImages?.length > 0) {
      offer.galleryImages.forEach(url => {
        images.push({ url, type: 'gallery' });
      });
    }
    // Fallback to product lifestyle image
    if (images.length === 0 && product?.lifestyleImageUrl) {
      images.push({ url: product.lifestyleImageUrl, type: 'product' });
    }
    return images;
  };

  const images = getAllImages();

  if (showLoading) {
    return <OfferLoadingScreen onComplete={handleAnimationComplete} />;
  }

  if (error || !offer) {
    return (
      <div className={styles.page}>
        <div className={styles.error}>
          <h2>Offer Not Found</h2>
          <p>{error || 'This product offer does not exist or has been removed.'}</p>
          <button onClick={() => navigate('/catalog')}>View Catalog</button>
        </div>
      </div>
    );
  }

  if (!offer.isActive) {
    return (
      <div className={styles.page}>
        <div className={styles.error}>
          <h2>Offer Unavailable</h2>
          <p>This offer is currently not active.</p>
          <button onClick={() => navigate('/catalog')}>View Catalog</button>
        </div>
      </div>
    );
  }

  const features = offer.features || [];

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* Hero Image Section */}
        {images.length > 0 && (
          <div className={styles.heroSection}>
            <div className={styles.heroImage}>
              <img
                src={images[selectedGalleryIndex]?.url}
                alt={offer.title || product?.name}
              />
            </div>
            {images.length > 1 && (
              <div className={styles.thumbnails}>
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    className={`${styles.thumbnail} ${idx === selectedGalleryIndex ? styles.thumbnailActive : ''}`}
                    onClick={() => setSelectedGalleryIndex(idx)}
                  >
                    <img loading="lazy" src={img.url} alt={`Thumbnail ${idx + 1}`} />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Content Section */}
        <div className={styles.contentSection}>
          {/* Badge */}
          {offer.badgeText && (
            <span className={styles.badge}>{offer.badgeText}</span>
          )}

          {/* Title */}
          <h1 className={styles.title}>
            {offer.title || product?.name}
            <span className={styles.rxSymbol}>℞</span>
          </h1>

          {/* Description */}
          {offer.description && (
            <p className={styles.description}>{offer.description}</p>
          )}

          {/* Features List */}
          {features.length > 0 && (
            <ul className={styles.featuresList}>
              {features.map((feature, index) => (
                <li key={index} className={styles.featureItem}>
                  <svg className={styles.checkIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Pricing Display */}
          {offer.pricingDisplay && (
            <p className={styles.pricing}>{offer.pricingDisplay}</p>
          )}

          {/* Supply Duration Selector */}
          <div style={{ margin: '16px 0' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '8px' }}>Supply Duration</label>
            {(() => {
              const activeDosages = (product?.dosages || []).filter(d => d.isActive !== false);
              const defaultDosage = activeDosages.find(d => d.isDefault) || activeDosages[0];
              return (
                <SupplyMonthSelector
                  pricePerMonth={applyPromoDiscount(
                    defaultDosage?.price || 0,
                    product?.systemPromo,
                    1
                  )}
                  supplyPricing={getAdjustedSupplyPricing(
                    defaultDosage?.supplyPricing,
                    defaultDosage?.price || 0,
                    product?.systemPromo
                  )}
                  selectedMonths={supplyMonths}
                  onChange={setSupplyMonths}
                />
              );
            })()}
          </div>

          {/* CTA Button */}
          <button
            className={styles.ctaButton}
            onClick={handleCTAClick}
            disabled={checkingIntake}
          >
            {checkingIntake ? 'Checking...' : (offer.ctaText || 'Get Started')}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </button>

          <ConsentDisclaimer />
        </div>
      </div>

      {/* Mobile Sticky CTA */}
      <div className={styles.stickyCtaMobile}>
        <div className={styles.stickyCtaContent}>
          {offer.pricingDisplay && (
            <span className={styles.stickyPrice}>{offer.pricingDisplay}</span>
          )}
          <button
            className={styles.stickyCtaButton}
            onClick={handleCTAClick}
            disabled={checkingIntake}
          >
            {checkingIntake ? 'Checking...' : (offer.ctaText || 'Get Started')}
          </button>
        </div>
      </div>

      {/* Loading overlay when checking intake status */}
      {checkingIntake && (
        <div className={styles.intakeCheckOverlay}>
          <div className={styles.intakeCheckSpinner} />
          <span>Checking your intake status...</span>
        </div>
      )}
    </div>
  );
};

export default SingleProductOfferPage;
